import { isEmpty, isEqual } from 'lodash';
import moment from 'moment';
import { useCallback, useContext, useMemo } from 'react';
import { RRule } from 'rrule';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBListRow } from '@shared/components/list/row';
import { SelectableRadioDot } from '@shared/components/list/row/selectable';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import * as RecurrencePresets from '../presets';
import { fromRRuleDate } from '../utils';
import { CustomSelectionType } from './CustomSelectionType';
import { DescriptiveRecurrenceHeader } from './components/DescriptiveRecurrenceHeader';

const SHOW_DEBUG_OPTIONS = import.meta.env.DEV;

interface Props {
  rruleString: string | undefined;
  onChange(rule: RRule | undefined): void;
  onSelectCustom(type: CustomSelectionType): void;
  onSelectEndDate(): void;
  onSelectCount(): void;
}

interface RowProps {
  title: string;
  selected: boolean;
  expandable?: boolean;
  onPress(): void;
}

function Row({ title, selected, expandable = false, onPress }: RowProps) {
  return (
    <MBListRow
      title={title}
      image={
        <View fill center>
          <SelectableRadioDot selected={selected} radio />
        </View>
      }
      isHidingPressableIndicator={!expandable}
      onPress={onPress}
    />
  );
}

interface RuleRowProps {
  title: string;
  rule: RRule | undefined;
  selected: boolean;
  onChange: Props['onChange'];
}

function RuleRow({ title, selected, rule, onChange }: RuleRowProps) {
  return (
    <Row
      title={title}
      selected={selected}
      onPress={useCallback(() => onChange(rule), [onChange, rule])}
    />
  );
}

const daily = RecurrencePresets.daily();
const weekdays = RecurrencePresets.weekdays();
const every2min = RecurrencePresets.every2min();
const every15min = RecurrencePresets.every15min();

export function MBRecurrencePickerStart({
  rruleString,
  onChange,
  onSelectCustom,
  onSelectEndDate,
  onSelectCount,
}: Props) {
  const rule = useMemo(
    () => (!!rruleString ? RRule.fromString(rruleString) : undefined),
    [rruleString],
  );
  const colors = useContext(ColorThemeContext);
  const weekdaysSelected = useMemo(
    () =>
      rule?.options.freq === RRule.WEEKLY &&
      isEqual(rule.options.byweekday.sort(), [
        RRule.MO.weekday,
        RRule.TU.weekday,
        RRule.WE.weekday,
        RRule.TH.weekday,
        RRule.FR.weekday,
      ]),
    [rule],
  );
  const weekendsSelected = useMemo(
    () =>
      rule?.options.freq === RRule.WEEKLY &&
      isEqual(rule.options.byweekday.sort(), [RRule.SA.weekday, RRule.SU.weekday]),
    [rule],
  );

  const showDescriptionTop = useMemo(() => {
    const everyDaySelected =
      !!rule && rule.options.freq === RRule.DAILY && rule.options.interval === 1;
    return everyDaySelected || weekdaysSelected;
  }, [rule, weekdaysSelected]);

  const until = useMemo(
    () => (!!rule?.options.until ? fromRRuleDate(rule.options.until) : undefined),
    [rule],
  );

  return (
    <>
      {showDescriptionTop && rule && <DescriptiveRecurrenceHeader rule={rule} />}
      <RuleRow
        title={i18n('card.blocks.recurrenceInput.actionSheet.never')}
        rule={undefined}
        selected={!rule}
        onChange={onChange}
      />
      <RuleRow
        title={i18n('card.blocks.recurrenceInput.actionSheet.everyDay')}
        rule={daily}
        selected={!!rule && rule.options.freq === RRule.DAILY && rule.options.interval === 1}
        onChange={onChange}
      />
      <RuleRow
        title={i18n('card.blocks.recurrenceInput.actionSheet.everyWeekDay')}
        rule={weekdays}
        selected={weekdaysSelected}
        onChange={onChange}
      />
      {SHOW_DEBUG_OPTIONS && (
        <RuleRow
          title="Testing: every 2 minutes"
          rule={every2min}
          selected={!!rule && rule.options.freq === RRule.MINUTELY && rule.options.interval === 2}
          onChange={onChange}
        />
      )}
      {SHOW_DEBUG_OPTIONS && (
        <RuleRow
          title="Testing: every 15 minutes"
          rule={every15min}
          selected={!!rule && rule.options.freq === RRule.MINUTELY && rule.options.interval === 15}
          onChange={onChange}
        />
      )}

      <View paddingHorizontal={2} paddingBottom={2}>
        <View style={{ height: 1, backgroundColor: colors.backgroundInactive }} />
      </View>

      <View paddingHorizontal={3} paddingVertical={0.5}>
        <Text size="body2" weight="semibold">
          {i18n('card.blocks.recurrenceInput.actionSheet.custom')}
        </Text>
      </View>

      {rule && !showDescriptionTop && !isEmpty(rule) && <DescriptiveRecurrenceHeader rule={rule} />}

      <Row
        title={i18n('card.blocks.recurrenceInput.actionSheet.daily')}
        expandable
        selected={rule?.options.freq === RRule.DAILY && rule.options.interval > 1}
        onPress={() => onSelectCustom(CustomSelectionType.DAILY)}
      />
      <Row
        title={i18n('card.blocks.recurrenceInput.actionSheet.weekly')}
        expandable
        selected={rule?.options.freq === RRule.WEEKLY && !weekdaysSelected && !weekendsSelected}
        onPress={() => onSelectCustom(CustomSelectionType.WEEKLY)}
      />
      <Row
        title={i18n('card.blocks.recurrenceInput.actionSheet.monthly')}
        expandable
        selected={rule?.options.freq === RRule.MONTHLY}
        onPress={() => onSelectCustom(CustomSelectionType.MONTHLY)}
      />
      <Row
        title={i18n('card.blocks.recurrenceInput.actionSheet.yearly')}
        expandable
        selected={rule?.options.freq === RRule.YEARLY}
        onPress={() => onSelectCustom(CustomSelectionType.YEARLY)}
      />

      {!!rule && (
        <>
          <View paddingHorizontal={2} paddingBottom={2}>
            <View style={{ height: 1, backgroundColor: colors.backgroundInactive }} />
          </View>
          <View paddingHorizontal={3} paddingVertical={0.5}>
            <Text size="body2" weight="semibold">
              {i18n('card.blocks.recurrenceInput.ends.title')}
            </Text>
          </View>
          <Row
            title={
              !!rule?.options.count
                ? i18n('card.blocks.recurrenceInput.ends.afterXTimes.selected', {
                    count: rule.options.count,
                  })
                : i18n('card.blocks.recurrenceInput.ends.afterXTimes.unselected')
            }
            selected={!!rule?.options.count}
            expandable
            onPress={onSelectCount}
          />
          <Row
            title={
              !!until
                ? i18n('card.blocks.recurrenceInput.ends.specificDate.selected', {
                    date: moment(until).format('ll'),
                  })
                : i18n('card.blocks.recurrenceInput.ends.specificDate.unselected')
            }
            selected={!!until}
            expandable
            onPress={onSelectEndDate}
          />
        </>
      )}
    </>
  );
}
